import { Trans, useTranslation } from 'react-i18next'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { textContrastIsAA } from 'helpers/getContrast'

import Avatar from 'js/components/Avatar/Avatar'
import DropdownButton from 'js/components/DropdownButton'
import { GET_TOP_MENU_AND_FOOTER } from 'js/layout/Layout'
import NavigationAction from 'js/layout/Navigation/components/NavigationAction'
import useIsPublishRequestsEnabled from 'js/user/helpers/useIsPublishRequestsEnabled'

import styles from './NavigationUserMenu.module.css'

type User = {
    username: string
    icon?: string
    name: string
    isOnline: boolean
    onlineStatus: 'auto' | 'offline'
    onlineStatusUpdatedAt?: string
}

type Viewer = {
    user: User
    canAccessAdmin: boolean
    updateAccountUrl: string
}

type NavigationUserMenuProps = {
    viewer: Viewer
}

const NavigationUserMenu = ({ viewer }: NavigationUserMenuProps) => {
    const { t } = useTranslation()

    const headerMain = getComputedStyle(document.documentElement)
        .getPropertyValue('--ref-palette-header-main')
        .trim()

    const { isPublishRequestsAnyEnabled } = useIsPublishRequestsEnabled()

    const { user, canAccessAdmin, updateAccountUrl } = viewer
    const { username, icon, name, isOnline, onlineStatus } = user

    const status = t(
        onlineStatus === 'auto'
            ? 'user.online-status.offline'
            : 'user.online-status.online',
    )
    const [setUserOnlineStatus, { loading: setUserOnlineStatusLoading }] =
        useMutation(SET_USER_ONLINE_STATUS)

    const handleSetUserOnlineStatus = () => {
        setUserOnlineStatus({
            variables: {
                input: {
                    status: onlineStatus === 'auto' ? 'offline' : 'auto',
                },
            },
            refetchQueries: [GET_TOP_MENU_AND_FOOTER],
        }).catch((error) => {
            console.error(error)
            throw new Error(error)
        })
    }

    const options = [
        [
            {
                label: (
                    <Trans
                        i18nKey="user.set-online-status"
                        values={{ status }}
                        components={{ strong: <strong /> }}
                    />
                ),
                onClick: handleSetUserOnlineStatus,
                disabled: setUserOnlineStatusLoading,
                disableCloseAfterClick: true,
            },
        ],
        [
            {
                label: t('action.bookmarked'),
                url: '/saved',
                navLink: true,
            },
            {
                label: t('user.my-files'),
                url: `/user/${username}/files`,
                navLink: true,
            },
            {
                label: t('user.contacts'),
                url: `/user/${username}/contacts`,
                navLink: true,
            },
        ],
        [
            {
                label: t('profile.title'),
                url: `/user/${username}/profile`,
                navLink: true,
            },
            {
                label: t('user.activity'),
                url: `/user/${username}/activity`,
                navLink: true,
            },
            ...(isPublishRequestsAnyEnabled
                ? [
                      {
                          label: t('content-moderation.publish-requests'),
                          url: `/user/${username}/publish-requests`,
                          navLink: true,
                      },
                  ]
                : []),
            {
                label: t('global.settings'),
                url: `/user/${username}/settings`,
                navLink: true,
            },
        ],
        [
            ...(canAccessAdmin
                ? [
                      {
                          label: t('global.admin-panel'),
                          url: `/admin`,
                      },
                  ]
                : []),
            {
                label: t('user.account'),
                url: updateAccountUrl,
                newWindow: true,
            },
            {
                label: t('action.logout'),
                url: '/logout',
            },
        ],
    ]

    const whitePassesAA = textContrastIsAA(headerMain)

    return (
        <DropdownButton
            arrow={true}
            side="bottom"
            animation="shift-away"
            options={options}
            positionIsFixed={true}
            ariaLabel={t('user.user-menu')}
        >
            <NavigationAction
                className={styles.userMenuButton}
                tooltip={t('user.user-menu')}
                aria-label={t('user.open-profile-menu')}
            >
                <Avatar
                    size="normal" // Defines outline width 2px
                    outline
                    outlineWidth={whitePassesAA ? 3 : 2}
                    outlineColor={whitePassesAA ? 'white' : 'black'}
                    image={icon}
                    name={name}
                    disabled
                    className="UserMenuAvatar"
                    onlineStatus={
                        isOnline === undefined
                            ? undefined
                            : isOnline
                              ? 'online'
                              : 'offline'
                    }
                />
            </NavigationAction>
        </DropdownButton>
    )
}

const SET_USER_ONLINE_STATUS = gql`
    mutation SetUserOnlineStatus($input: SetUserOnlineStatusInput!) {
        setUserOnlineStatus(input: $input) {
            success
        }
    }
`

export default NavigationUserMenu
